import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { generateDeviceFingerprint, getDeviceInfo } from "./fingerprint";

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Axios interceptor: attach token to every request
  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    const resInterceptor = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("auth_token");
          setUser(null);
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  // Fetch user on mount if token exists
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchUser(token) {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(data.user);
    } catch {
      localStorage.removeItem("auth_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  const login = useCallback(async (email, password) => {
    const deviceId = generateDeviceFingerprint();
    const deviceInfo = getDeviceInfo();
    const { data } = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
      device_id: deviceId,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      user_agent: deviceInfo.userAgent,
    });
    localStorage.setItem("auth_token", data.token);
    setUser(data.user);
    return data;
  }, []);

  const signup = useCallback(async (name, email, password) => {
    const deviceId = generateDeviceFingerprint();
    const deviceInfo = getDeviceInfo();
    const { data } = await axios.post(`${API_URL}/api/auth/signup`, {
      name,
      email,
      password,
      device_id: deviceId,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      user_agent: deviceInfo.userAgent,
    });
    localStorage.setItem("auth_token", data.token);
    setUser(data.user);
    return data; // caller checks data.trial_skipped + data.trial_message
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch {
      /* ignore */
    }
    localStorage.removeItem("auth_token");
    setUser(null);
  }, []);

  const logoutAllDevices = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout-all-devices`);
    } catch {
      /* ignore */
    }
    localStorage.removeItem("auth_token");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(data.user);
      } catch { /* silent */ }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, logoutAllDevices, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
